import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import {
  fetchProductionOrders,
  fetchProductionMonthLock,
  listProductionMonthsApi,
  fetchWorkUnits,
  approveProductionMonth,
  updateProductionOrder,
  updateProductionOrderPositions,
  ackProductionOrderChange,
  patchProductionOrderStatus,
  type ProductionOrder,
  type ProductionMonthLock,
  type WorkUnit,
} from '@/services/productionOrders'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import {
  sortRowsByFactoryCompletion,
  getFourProductionMonths,
  resolveProductionOrdersMonthFilter,
  resolveDefaultPlanningTab,
} from './productionOrderUtils'
import { ProductionMonthBar } from './ProductionMonthBar'
import { ProductionOrderGrid } from './ProductionOrderGrid'
import { WorkUnitsModal } from './WorkUnitsModal'
import { MonthReopenModal } from './MonthReopenModal'
import { MonthHistoryModal } from './MonthHistoryModal'
import { ProductionOrderLineHistoryModal } from './ProductionOrderLineHistoryModal'
import { ReturnToDraftModal } from './ReturnToDraftModal'
import { ProductionOrderPushToDemandModal } from './ProductionOrderPushToDemandModal'
import { ProductionDemandModal } from './ProductionDemandModal'
import type { PlanningTabId } from './productionOrderUtils'

export type PlanningTab = PlanningTabId

export function ProductionOrdersPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { hasRole, can } = useAuth()

  // RBAC checks
  // SALE role does not have planning.write and cannot edit/drag/approve
  const canWrite =
    can('planning.write') || hasRole('APPROVER') || hasRole('ADMIN')
  const canApprove =
    can('planning.approve') || hasRole('APPROVER') || hasRole('ADMIN')

  // E2: badge + default tab from unscheduled count (resolved once on mount)
  const [activeTab, setActiveTab] = useState<PlanningTab>('orders')
  const [inboxCount, setInboxCount] = useState(0)
  const [tabInitialized, setTabInitialized] = useState(false)

  // 4 months fallback list
  const availableMonths = useMemo(() => getFourProductionMonths(), [])

  // Current selected month: defaults to query param or 1st available month
  // Never treat "unscheduled" as a month option (that lives in the inbox tab).
  const selectedMonth = useMemo(() => {
    const fromUrl = searchParams.get('month')
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) {
      return fromUrl
    }
    return availableMonths[0]?.month || new Date().toISOString().slice(0, 7)
  }, [searchParams, availableMonths])

  // Data states
  const [orders, setOrders] = useState<ProductionOrder[]>([])
  const [monthLock, setMonthLock] = useState<ProductionMonthLock | null>(null)
  const [months, setMonths] = useState<ProductionMonthLock[]>([])
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Filters
  const [searchKeyword, setSearchKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')

  // Auto-sort toggle on save (default true)
  const [autoSortOnSave, setAutoSortOnSave] = useState(true)

  // Save history snapshot for undo
  const [lastSavedOrderIds, setLastSavedOrderIds] = useState<string[] | null>(null)
  const [flashingIds, setFlashingIds] = useState<string[]>([])

  // Modals state
  const [workUnitsModalOpen, setWorkUnitsModalOpen] = useState(false)
  const [monthReopenModalOpen, setMonthReopenModalOpen] = useState(false)
  const [monthHistoryModalOpen, setMonthHistoryModalOpen] = useState(false)
  const [lineHistoryOrder, setLineHistoryOrder] = useState<ProductionOrder | null>(null)
  const [returnToDraftOrder, setReturnToDraftOrder] = useState<ProductionOrder | null>(null)
  const [pushToDemandOrder, setPushToDemandOrder] = useState<ProductionOrder | null>(null)
  const [productionDemandOrder, setProductionDemandOrder] = useState<ProductionOrder | null>(null)

  // Toast message
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => {
      setToastMessage((cur) => (cur === msg ? null : cur))
    }, 3500)
  }, [])

  // Month change handler
  const handleSelectMonth = (newMonth: string) => {
    if (newMonth === 'unscheduled') return
    setSearchParams({ month: newMonth })
  }

  // Resolve default tab once from unscheduled inbox count
  useEffect(() => {
    if (tabInitialized) return
    let cancelled = false
    fetchProductionOrders({ month: 'unscheduled' })
      .then((rows) => {
        if (cancelled) return
        setInboxCount(rows.length)
        setActiveTab(resolveDefaultPlanningTab(rows.length))
      })
      .catch(() => {
        if (cancelled) return
        setActiveTab('orders')
      })
      .finally(() => {
        if (!cancelled) setTabInitialized(true)
      })
    return () => {
      cancelled = true
    }
  }, [tabInitialized])

  // Load data — inbox tab always uses API month=unscheduled (no month picker)
  const loadData = useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      const monthFilter = resolveProductionOrdersMonthFilter(activeTab, selectedMonth)

      const [ordersRes, lockRes, unitsRes, monthsRes, inboxRes] = await Promise.all([
        fetchProductionOrders({
          month: monthFilter,
          q: searchKeyword || undefined,
          status: statusFilter !== 'ALL' ? statusFilter : undefined,
        }),
        activeTab === 'orders'
          ? fetchProductionMonthLock(selectedMonth).catch(() => null)
          : Promise.resolve(null),
        fetchWorkUnits().catch(() => []),
        listProductionMonthsApi().catch(() => []),
        // Keep badge count fresh even while viewing the orders tab
        activeTab === 'inbox'
          ? Promise.resolve(null)
          : fetchProductionOrders({ month: 'unscheduled' }).catch(() => []),
      ])

      setOrders(ordersRes)
      setMonthLock(lockRes)
      setWorkUnits(unitsRes)
      setMonths(monthsRes)
      if (activeTab === 'inbox') {
        setInboxCount(ordersRes.length)
      } else if (Array.isArray(inboxRes)) {
        setInboxCount(inboxRes.length)
      }
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setErrorMsg(formatted.message)
    } finally {
      setLoading(false)
    }
  }, [activeTab, selectedMonth, searchKeyword, statusFilter])

  useEffect(() => {
    if (!tabInitialized) return
    loadData()
  }, [loadData, tabInitialized])

  // Month approval action (orders tab only)
  const handleApproveMonth = async () => {
    if (!canApprove || activeTab !== 'orders') return
    if (!window.confirm(`Xác nhận phê duyệt và khóa kế hoạch tháng ${selectedMonth}?`)) return
    try {
      await approveProductionMonth(selectedMonth)
      showToast(`✓ Đã phê duyệt kế hoạch tháng ${selectedMonth}`)
      loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      showToast(`Lỗi: ${formatted.message}`)
    }
  }

  // Update order cell
  const handleUpdateOrder = async (id: string, updates: Partial<ProductionOrder>) => {
    // Optimistic update local state
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, ...updates } : o)),
    )

    const locked = activeTab === 'orders' && monthLock?.status === 'approved'
    if (!canWrite || locked) return

    try {
      await updateProductionOrder(id, updates)
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      showToast(`Lỗi lưu dòng: ${formatted.message}`)
      loadData()
    }
  }

  // Reorder local state
  const handleReorder = (newOrders: ProductionOrder[]) => {
    setOrders(newOrders)
  }

  // Save plan action
  const handleSavePlan = async () => {
    const locked = activeTab === 'orders' && monthLock?.status === 'approved'
    if (!canWrite || locked) return

    setSaving(true)
    try {
      // Snapshot previous IDs for undo
      const prevIds = orders.map((o) => o.id)
      setLastSavedOrderIds(prevIds)

      let targetOrders = [...orders]
      if (autoSortOnSave) {
        targetOrders = sortRowsByFactoryCompletion(orders)
      }

      // Detect moved rows to flash
      const movedIds: string[] = []
      targetOrders.forEach((item, idx) => {
        if (prevIds[idx] !== item.id) {
          movedIds.push(item.id)
        }
      })

      // Send to server
      const newIds = targetOrders.map((o) => o.id)
      await updateProductionOrderPositions(newIds, autoSortOnSave)

      setOrders(targetOrders)
      showToast('✓ Đã lưu kế hoạch sản xuất thành công!')

      // Trigger flash animation for 2 seconds
      if (movedIds.length > 0) {
        setFlashingIds(movedIds)
        setTimeout(() => {
          setFlashingIds([])
        }, 2000)
      }
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      showToast(`Lỗi lưu kế hoạch: ${formatted.message}`)
    } finally {
      setSaving(false)
    }
  }

  // Undo last save action
  const handleUndoLastSave = async () => {
    if (!lastSavedOrderIds || lastSavedOrderIds.length === 0) return
    const locked = activeTab === 'orders' && monthLock?.status === 'approved'
    if (!canWrite || locked) return

    setSaving(true)
    try {
      const orderMap = new Map(orders.map((o) => [o.id, o]))
      const restoredOrders: ProductionOrder[] = []

      lastSavedOrderIds.forEach((id) => {
        const item = orderMap.get(id)
        if (item) restoredOrders.push(item)
      })

      // Append any new orders not in snapshot
      orders.forEach((o) => {
        if (!lastSavedOrderIds.includes(o.id)) {
          restoredOrders.push(o)
        }
      })

      await updateProductionOrderPositions(
        restoredOrders.map((o) => o.id),
        false,
      )

      setOrders(restoredOrders)
      setLastSavedOrderIds(null)
      showToast('✓ Đã hoàn tác thứ tự lần lưu gần nhất.')
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      showToast(`Lỗi hoàn tác: ${formatted.message}`)
    } finally {
      setSaving(false)
    }
  }

  // Ack change on line
  const handleAckChange = async (id: string) => {
    try {
      await ackProductionOrderChange(id)
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, source_changed: false } : o)),
      )
      showToast('✓ Đã xác nhận thay đổi đơn.')
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      showToast(`Lỗi xác nhận: ${formatted.message}`)
    }
  }

  // Return line to draft
  const handleConfirmReturnToDraft = async (reason: string) => {
    if (!returnToDraftOrder) return
    try {
      await patchProductionOrderStatus(returnToDraftOrder.id, 'draft', reason)
      showToast(`✓ Đã hoàn dòng ${returnToDraftOrder.variant_code} về Nháp.`)
      loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      showToast(`Lỗi: ${formatted.message}`)
    }
  }

  const monthLocked = activeTab === 'orders' && monthLock?.status === 'approved'

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 px-4 py-2 sm:px-6 lg:px-8">
      <div className="shrink-0 space-y-2">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-[99999999] rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-xl animate-in slide-in-from-top-3 duration-200">
          {toastMessage}
        </div>
      )}

      <div>
        <h1 className="text-xl font-bold text-slate-900">Lệnh sản xuất</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Bảng kế hoạch điều độ (không phải phân hệ Production Order/Execution đã hoãn).
        </p>
      </div>

      {/* Planning tabs — under title, above ProductionMonthBar */}
      <div
        className="flex gap-1 border-b border-slate-200"
        role="tablist"
        aria-label="Kế hoạch"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'inbox'}
          data-testid="planning-tab-inbox"
          onClick={() => setActiveTab('inbox')}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === 'inbox'
              ? 'border-emerald-600 text-emerald-800'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Nhận từ Sale
          {inboxCount > 0 && (
            <span
              data-testid="planning-inbox-badge"
              className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-xs font-bold leading-none text-white"
              aria-label={`${inboxCount} dòng chờ xếp lịch`}
            >
              {inboxCount}
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'orders'}
          data-testid="planning-tab-orders"
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === 'orders'
              ? 'border-emerald-600 text-emerald-800'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Lệnh sản xuất
        </button>
      </div>

      {/* Month Bar — only on "Lệnh sản xuất" tab */}
      {activeTab === 'orders' && (
        <ProductionMonthBar
          selectedMonth={selectedMonth}
          months={months}
          orders={orders}
          currentLock={monthLock}
          canWrite={canWrite}
          canApprove={canApprove}
          onSelectMonth={handleSelectMonth}
          onApproveMonth={handleApproveMonth}
          onOpenReopenModal={() => setMonthReopenModalOpen(true)}
          onOpenHistoryModal={() => setMonthHistoryModalOpen(true)}
          onOpenWorkUnitsModal={() => setWorkUnitsModalOpen(true)}
        />
      )}

      {/* Filter and Search Bar (both tabs) */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm text-xs">
        <div className="flex items-center gap-3 flex-wrap">
          {activeTab === 'inbox' && (
            <p className="text-slate-600">
              Dòng sẽ tự chuyển sang Lệnh sản xuất khi nhập đủ ngày Hoàn thành nhà máy.
            </p>
          )}
          <input
            type="text"
            placeholder="Tìm theo mã hàng, tên sản phẩm, PO, khách..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="w-full sm:w-72 rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-900 focus:border-emerald-500 outline-none"
          />

          <div className="flex items-center gap-1">
            <span className="text-slate-500 font-medium">Trạng thái:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-800 bg-white focus:border-emerald-500 outline-none"
            >
              <option value="ALL">Tất cả trạng thái</option>
              <option value="on_track">Đúng tiến độ</option>
              <option value="at_risk">Nguy cơ trễ</option>
              <option value="late">Trễ hạn</option>
              <option value="done">Hoàn tất</option>
              {activeTab === 'orders' && (
                <option value="unscheduled">Chưa lên lịch</option>
              )}
            </select>
          </div>
        </div>

        <div className="text-slate-500 text-xs">
          Hiển thị <strong>{orders.length}</strong> dòng
        </div>
      </div>

      {/* Error Banner */}
      {errorMsg && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800 font-medium">
          {errorMsg}
        </div>
      )}
      </div>

      {/* Grid — same ProductionOrderGrid for both tabs; only data filter differs */}
      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm">
          <p className="text-sm text-slate-500 font-medium animate-pulse">
            Đang tải dữ liệu kế hoạch sản xuất...
          </p>
        </div>
      ) : (
        <ProductionOrderGrid
          orders={orders}
          workUnits={workUnits}
          canWrite={canWrite}
          monthLocked={monthLocked}
          onUpdateOrder={handleUpdateOrder}
          onReorder={handleReorder}
          onSavePlan={handleSavePlan}
          isSaving={saving}
          autoSortOnSave={autoSortOnSave}
          onToggleAutoSort={setAutoSortOnSave}
          onUndoLastSave={handleUndoLastSave}
          canUndo={Boolean(lastSavedOrderIds && lastSavedOrderIds.length > 0)}
          onAckChange={handleAckChange}
          onViewHistory={(order) => setLineHistoryOrder(order)}
          onReturnToDraft={(order) => setReturnToDraftOrder(order)}
          onPushToDemand={(order) => setPushToDemandOrder(order)}
          onOpenProductionDemand={(order) => setProductionDemandOrder(order)}
          onOpenExecution={(order) =>
            navigate(`/planning/production-orders/${encodeURIComponent(order.id)}/execution`)
          }
          flashingIds={flashingIds}
        />
      )}

      {/* Modals */}
      <WorkUnitsModal
        isOpen={workUnitsModalOpen}
        onClose={() => {
          setWorkUnitsModalOpen(false)
          fetchWorkUnits().then(setWorkUnits).catch(() => {})
        }}
      />

      <MonthReopenModal
        isOpen={monthReopenModalOpen}
        month={selectedMonth}
        lockInfo={monthLock}
        onClose={() => setMonthReopenModalOpen(false)}
        onSuccess={() => {
          setMonthReopenModalOpen(false)
          showToast(`✓ Đã mở lại kế hoạch tháng ${selectedMonth}`)
          loadData()
        }}
      />

      <MonthHistoryModal
        isOpen={monthHistoryModalOpen}
        month={selectedMonth}
        onClose={() => setMonthHistoryModalOpen(false)}
      />

      <ProductionOrderLineHistoryModal
        isOpen={Boolean(lineHistoryOrder)}
        order={lineHistoryOrder}
        row={lineHistoryOrder}
        onClose={() => setLineHistoryOrder(null)}
      />

      <ReturnToDraftModal
        isOpen={Boolean(returnToDraftOrder)}
        order={returnToDraftOrder}
        onClose={() => setReturnToDraftOrder(null)}
        onConfirm={handleConfirmReturnToDraft}
      />

      <ProductionOrderPushToDemandModal
        isOpen={Boolean(pushToDemandOrder)}
        order={pushToDemandOrder}
        onClose={() => setPushToDemandOrder(null)}
        onSuccess={() => {
          showToast('✓ Đã đưa vào đợt nhu cầu thành công!')
        }}
      />

      <ProductionDemandModal
        isOpen={Boolean(productionDemandOrder)}
        order={productionDemandOrder}
        onClose={() => setProductionDemandOrder(null)}
        onSuccess={(msg) => {
          showToast(`✓ ${msg}`)
          loadData()
        }}
      />
    </div>
  )
}
